import { Routes, Route, Navigate, useParams } from 'react-router-dom'
import { Suspense, lazy } from 'react'
import { useAuth } from './hooks/useAuth'
const Layout = lazy(() => import('./layouts/Layout'))
const HomePage = lazy(() => import('./pages/Home'))
const AboutPage = lazy(() => import('./pages/About'))
const ExplorePage = lazy(() => import('./pages/Explore'))
const TourDetailPage = lazy(() => import('./pages/TourDetail'))
const TourNavigationPage = lazy(() => import('./pages/TourNavigation'))
const CheckoutPage = lazy(() => import('./pages/Checkout'))
const AccountPage = lazy(() => import('./pages/Account'))
const LoginPage = lazy(() => import('./pages/Login'))
const SignupPage = lazy(() => import('./pages/Signup'))
const TourListPage = lazy(() => import('./pages/Studio'))
const TourFormPage = lazy(() => import('./pages/Studio/Form'))
const StudioTourDetailPage = lazy(() => import('./pages/Studio/TourDetail'))
const POIManagementPage = lazy(() => import('./pages/Studio/POI/Management'))
const StudioRoutePage = lazy(() => import('./pages/Studio/Route'))
const EmailConfirmation = lazy(() => import('./pages/EmailConfirmation'))
const RequestPasswordReset = lazy(() => import('./pages/PasswordReset'))
const ResetPassword = lazy(() => import('./pages/PasswordSet'))
const InternalTrackingPage = lazy(() => import('./pages/InternalTracking'))
const CreatorProfilePage = lazy(() => import('./pages/CreatorProfile'))
const CreatorProfileEditPage = lazy(() => import('./pages/CreatorProfileEdit'))
import type { UserRole } from './types'
const NotFoundPage = lazy(() => import('./pages/Notfound'))

function ProtectedRoute({
  children,
  roles,
  guestOnly = false
}: {
  children: React.ReactNode
  roles?: UserRole[]
  guestOnly?: boolean
}) {
  const { user, loading, hydrated, isRole } = useAuth()
  const { lang } = useParams()

  if (loading) {
    return <></>
  }

  if (guestOnly) {
    // For guest-only routes, redirect authenticated users to account
    if (hydrated && user) {
      return <Navigate to={`/${lang}/account`} replace={true} />
    }
  } else {
    // For protected routes, redirect unauthenticated users to login
    if (hydrated && !user) {
      return <Navigate to={`/${lang}/login`} replace={true} />
    }

    if (hydrated && roles && !isRole(roles)) {
      return <Navigate to={`/${lang}`} replace={true} />
    }
  }

  if (!hydrated) {
    return <></>
  }

  return <>{children}</>
}

export default function AppRoutes() {
  return (
    <Suspense fallback={<></>}>
      <Routes>
        <Route path=":lang/explore/:id/navigate" element={<TourNavigationPage />} />
        <Route path=":lang/embed/:id" element={<TourNavigationPage />} />
        <Route
          path=":lang/studio/tours/:id/route"
          element={
            <ProtectedRoute roles={['admin', 'creator']}>
              <StudioRoutePage />
            </ProtectedRoute>
          }
        />
        <Route path=":lang" element={<Layout />}>
          <Route index element={<HomePage />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="explore" element={<ExplorePage />} />
          <Route path="explore/:city/all" element={<ExplorePage />} />
          <Route path="explore/all/:category" element={<ExplorePage />} />
          <Route path="explore/:city/:category" element={<ExplorePage />} />
          <Route path="explore/:id" element={<TourDetailPage />} />
          <Route path="creators/:creatorId" element={<CreatorProfilePage />} />
          <Route path="creators/:creatorId/edit" element={<CreatorProfileEditPage />} />
          <Route
            path="explore/:id/checkout"
            element={
              <ProtectedRoute>
                <CheckoutPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="account"
            element={
              <ProtectedRoute>
                <AccountPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="login"
            element={
              <ProtectedRoute guestOnly>
                <LoginPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="signup"
            element={
              <ProtectedRoute guestOnly>
                <SignupPage />
              </ProtectedRoute>
            }
          />
          <Route path="request-password-reset" element={<RequestPasswordReset />} />
          <Route path="email-confirmation" element={<EmailConfirmation />} />
          <Route path="tracking" element={<InternalTrackingPage />} />

          {/* Studio routes nested under the same Layout */}
          <Route
            path="studio"
            element={
              <ProtectedRoute roles={['admin', 'creator']}>
                <TourListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="studio/tours"
            element={
              <ProtectedRoute roles={['admin', 'creator']}>
                <TourListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="studio/tours/new"
            element={
              <ProtectedRoute roles={['admin', 'creator']}>
                <TourFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="studio/tours/:id"
            element={
              <ProtectedRoute roles={['admin', 'creator']}>
                <StudioTourDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="studio/tours/:id/points"
            element={
              <ProtectedRoute roles={['admin', 'creator']}>
                <POIManagementPage />
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<NotFoundPage />} />
        </Route>
        <Route path="/reset-password" element={<ResetPassword />} />
      </Routes>
    </Suspense>
  )
}
